import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";
import { usePageContent, getSection } from "@/hooks/usePageContent";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SEO from "@/components/SEO";
import EventRegistrationForm from "@/components/EventRegistrationForm";
import { toast } from "sonner";
import { 
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Users,
  Video,
  Globe,
  Mic,
  BookOpen,
  ChevronRight,
  Loader2,
  Share2,
  ExternalLink,
  Bell,
  CheckCircle,
  Facebook,
  Twitter,
  Linkedin,
  Link as LinkIcon
} from "lucide-react";

const API_URL = window.location.origin;

const DEFAULT_EVENT_IMAGE = 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800';

// Event type icons
const eventTypeIcons = {
  expo: Globe,
  fair: Globe,
  webinar: Video,
  workshop: BookOpen,
  seminar: Mic,
  open_day: Users,
  other: Calendar
};

// Event type colors
const eventTypeColors = {
  expo: 'bg-ux-violet text-white',
  fair: 'bg-ux-violet text-white',
  webinar: 'bg-ux-cyan text-white',
  workshop: 'bg-ux-coral text-white',
  seminar: 'bg-ux-mint text-white',
  open_day: 'bg-ux-amber text-white',
  other: 'bg-gray-500 text-white'
};

export const EventDetailPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [email, setEmail] = useState('');
  const [registered, setRegistered] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);

  // CMS Template Strings (Phase D - shared detail template)
  const { content: cmsContent } = usePageContent('events-detail');
  const t = getSection(cmsContent, 'template_strings', {
    about_header: 'About This Event',
    highlights_header: 'Key Highlights',
    universities_header: 'Participating Universities',
    speaker_header: 'Speaker',
    tags_header: 'Event Tags',
    share_label: 'Share this event:',
    back_label: 'All Events',
    register_card_title: 'Register for this Event',
    register_button: 'Register Now',
    seats_label: 'Limited to {n} seats',
    redirect_note: "You'll be redirected to the registration page",
    registered_title: "You're Registered!",
    registered_subtitle: 'Check your email for details.',
    related_header: 'Related Events'
  });

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const res = await fetch(`${API_URL}/api/public/events/${eventId}`);
        if (!res.ok) {
          if (res.status === 404) {
            setError("Event not found");
          } else {
            throw new Error("Failed to fetch event");
          }
          return;
        }
        
        const data = await res.json();
        setEvent(data);
      } catch (e) {
        console.error('Failed to fetch event:', e);
        setError("Failed to load event");
      } finally {
        setLoading(false);
      }
    };
    
    if (eventId) {
      fetchEvent();
    }
  }, [eventId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric',
      month: 'long', 
      day: 'numeric'
    });
  };

  const isUpcoming = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) >= new Date();
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    
    setRegistering(true);
    
    // Simulate registration (would connect to actual API)
    setTimeout(() => {
      setRegistered(true);
      setRegistering(false);
      toast.success("Successfully registered for the event!");
    }, 1000);
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = event?.title || '';
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      copy: url
    };
    
    if (platform === 'copy') {
      navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard!");
    } else {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen pt-20 flex items-center justify-center ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-ux-cyan" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className={`min-h-screen pt-20 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <Calendar className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-white/30' : 'text-gray-300'}`} />
          <h1 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
            {error || "Event not found"}
          </h1>
          <p className={`mb-8 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
            The event you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={() => navigate('/events')} className="bg-ux-cyan hover:bg-ux-blue text-white">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Events
          </Button>
        </div>
      </div>
    );
  }

  const eventType = event.event_type || event.type || 'other';
  const TypeIcon = eventTypeIcons[eventType] || Calendar;
  const eventDate = event.event_date || event.date;
  const eventTime = event.event_time || event.time;
  const maxAttendees = event.max_attendees;
  const registrationLink = event.registration_link;
  const tags = event.tags || [];
  const universities = event.universities || [];
  const upcoming = isUpcoming(eventDate);
  
  // Use highlights from database, or fallback to parsing from description
  const getHighlights = () => {
    // If highlights array exists in database, use it
    if (event.highlights && Array.isArray(event.highlights) && event.highlights.length > 0) {
      return { about: event.description, highlights: event.highlights };
    }
    
    // Fallback: Parse description to extract highlights (bullet points)
    const desc = event.description;
    if (!desc) return { about: '', highlights: [] };
    
    let about = desc;
    let highlights = [];
    
    // Try to split at numbered points (1. 2. 3.) or bullet patterns
    const numberedPattern = /\s*\d+\.\s+/g;
    const hasNumberedList = numberedPattern.test(desc);
    
    if (hasNumberedList) {
      // Find where bullet points start
      const parts = desc.split(/(?=\d+\.\s)/);
      if (parts.length > 1) {
        // First part is the "about" section
        about = parts[0].trim();
        // Rest are highlights
        highlights = parts.slice(1).map(p => p.replace(/^\d+\.\s*/, '').trim()).filter(h => h.length > 5);
      }
    }
    
    return { about, highlights };
  };
  
  const { about: aboutText, highlights } = getHighlights();

  return (
    <div className={`min-h-screen pt-20 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`} data-testid="event-detail-page">
      {/* SEO Meta Tags */}
      <SEO 
        title={`${event.title} | UniXperts Events`}
        description={event.description || event.title}
        keywords={tags.join(', ') || `study abroad event, ${eventType}`}
        canonical={`https://unixperts.com/events/${eventId}`}
        ogImage={event.featured_image || event.image || DEFAULT_EVENT_IMAGE}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Event",
          "name": event.title,
          "description": event.description,
          "image": event.featured_image || event.image || DEFAULT_EVENT_IMAGE,
          "startDate": eventDate,
          "endDate": event.end_date || eventDate,
          "eventAttendanceMode": event.is_online || event.mode === 'online'
            ? "https://schema.org/OnlineEventAttendanceMode"
            : "https://schema.org/OfflineEventAttendanceMode",
          "eventStatus": upcoming 
            ? "https://schema.org/EventScheduled" 
            : "https://schema.org/EventCancelled",
          "location": event.is_online || event.mode === 'online'
            ? { "@type": "VirtualLocation", "url": event.registration_link || `https://unixperts.com/events/${eventId}` }
            : { "@type": "Place", "name": event.location, "address": event.venue_address || event.location },
          "organizer": {
            "@type": "Organization",
            "name": "UniXperts",
            "url": "https://unixperts.com"
          },
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "INR",
            "availability": "https://schema.org/InStock",
            "url": `https://unixperts.com/events/${eventId}`
          }
        }}
      />

      {/* Hero Section */}
      <section className="relative">
        <div className="relative h-[50vh] md:h-[60vh]">
          <img 
            src={event.featured_image || event.image || DEFAULT_EVENT_IMAGE} 
            alt={event.title}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.src = DEFAULT_EVENT_IMAGE; }}
          />
          <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-t from-[#1a2830] via-[#1a2830]/70 to-transparent' : 'bg-gradient-to-t from-white via-black/50 to-transparent'}`} />
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 pb-8 md:pb-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm mb-4">
              <Link to="/" className="text-white/70 hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4 text-white/50" />
              <Link to="/events" className="text-white/70 hover:text-white transition-colors">Events</Link>
              <ChevronRight className="w-4 h-4 text-white/50" />
              <span className="text-white truncate max-w-[200px]">{event.title}</span>
            </nav>
            
            <div className="flex items-center gap-3 mb-4">
              <Badge className={eventTypeColors[eventType] || eventTypeColors.other}>
                <TypeIcon className="w-3 h-3 mr-1" />
                {eventType.charAt(0).toUpperCase() + eventType.slice(1).replace('_', ' ')}
              </Badge>
              {/* Event Status Badge */}
              {event.status === 'ongoing' && (
                <Badge className="bg-green-500 text-white">Ongoing</Badge>
              )}
              {event.status === 'upcoming' && (
                <Badge className="bg-blue-500 text-white">Upcoming</Badge>
              )}
              {event.status === 'completed' && (
                <Badge className="bg-gray-500 text-white">Completed</Badge>
              )}
              {event.status === 'cancelled' && (
                <Badge className="bg-red-500 text-white">Cancelled</Badge>
              )}
              {event.is_featured && (
                <Badge className="bg-ux-amber text-white">Featured</Badge>
              )}
            </div>
            
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
              {event.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-white/80">
              <span className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-ux-cyan" />
                {formatDate(eventDate)}
              </span>
              {eventTime && (
                <span className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-ux-violet" />
                  {eventTime}
                </span>
              )}
              <span className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-ux-coral" />
                {event.location || (event.is_online ? 'Online Event' : 'TBA')}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className={`py-12 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* About This Event */}
              <div>
                <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-ux-charcoal'}`} data-testid="event-about-header">
                  {t.about_header}
                </h2>
                <p className={`text-lg leading-relaxed ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                  {aboutText || event.description}
                </p>
              </div>
              
              {/* Highlights Section */}
              {highlights.length > 0 && (
                <div className={`glass-panel rounded-2xl p-6`}>
                  <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    {t.highlights_header}
                  </h2>
                  <ul className="space-y-3">
                    {highlights.map((highlight, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-ux-green mt-0.5 flex-shrink-0" />
                        <span className={`${isDark ? 'text-white/80' : 'text-gray-700'}`}>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Universities (if any) */}
              {universities.length > 0 && (
                <div>
                  <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    {t.universities_header}
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {universities.map((uni) => (
                      <Badge 
                        key={uni} 
                        variant="outline"
                        className={`px-4 py-2 text-sm ${isDark ? 'border-white/20 text-white' : 'border-gray-300'}`}
                      >
                        {uni}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Speaker Info (if any) */}
              {event.speaker && (
                <div className={`glass-panel rounded-2xl p-6`}>
                  <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    {t.speaker_header}
                  </h2>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-ux-violet flex items-center justify-center text-white text-xl font-bold">
                      {event.speaker[0]}
                    </div>
                    <div>
                      <p className={`font-bold text-lg ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                        {event.speaker}
                      </p>
                      {event.speakerRole && (
                        <p className={`${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                          {event.speakerRole}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Tags */}
              {tags.length > 0 && (
                <div>
                  <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    {t.tags_header}
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <Badge 
                        key={tag} 
                        className={`${isDark ? 'bg-ux-cyan/20 text-ux-cyan' : 'bg-ux-cyan/10 text-ux-cyan'}`}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Share Section */}
              <div className="pt-8 border-t border-gray-200 dark:border-white/10">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <p className={`font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    {t.share_label}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => handleShare('facebook')}
                      className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}
                    >
                      <Facebook className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => handleShare('twitter')}
                      className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}
                    >
                      <Twitter className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => handleShare('linkedin')}
                      className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}
                    >
                      <Linkedin className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => handleShare('copy')}
                      className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}
                    >
                      <LinkIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Sidebar - Registration */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Back to Events */}
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/events')}
                  className={`w-full ${isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}`}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t.back_label}
                </Button>
                
                {/* Registration Card */}
                {upcoming ? (
                  <div className={`glass-panel rounded-2xl p-6`}>
                    <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                      {t.register_card_title}
                    </h3>
                    
                    {/* Simple capacity indicator */}
                    {maxAttendees && (
                      <div className={`flex items-center gap-2 mb-4 text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                        <Users className="w-4 h-4" />
                        <span>{(t.seats_label || 'Limited to {n} seats').replace('{n}', maxAttendees)}</span>
                      </div>
                    )}
                    
                    {registrationLink ? (
                      <>
                        <Button 
                          asChild
                          className="w-full bg-gradient-to-r from-ux-cyan to-ux-blue hover:opacity-90 text-white"
                        >
                          <a href={registrationLink} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4 mr-2" />
                            {t.register_button}
                          </a>
                        </Button>
                        <p className={`text-xs text-center mt-3 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                          {t.redirect_note}
                        </p>
                      </>
                    ) : (
                      <>
                        {registered ? (
                          <div className="text-center py-4">
                            <CheckCircle className="w-12 h-12 text-ux-green mx-auto mb-2" />
                            <p className={`font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                              {t.registered_title}
                            </p>
                            <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                              {t.registered_subtitle}
                            </p>
                          </div>
                        ) : (
                          <Button 
                            onClick={() => setShowRegistrationForm(true)}
                            className="w-full bg-gradient-to-r from-ux-cyan to-ux-blue hover:opacity-90 text-white"
                          >
                            <Bell className="w-4 h-4 mr-2" />
                            {t.register_button}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <div className={`glass-panel rounded-2xl p-6 text-center`}>
                    <Calendar className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-white/30' : 'text-gray-300'}`} />
                    <p className={`font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                      This event has ended
                    </p>
                    <p className={`text-sm mt-2 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                      Check out our upcoming events for more opportunities.
                    </p>
                    <Button 
                      asChild
                      className="w-full mt-4 bg-ux-cyan hover:bg-ux-blue text-white text-sm"
                    >
                      <Link to="/events">Browse Events</Link>
                    </Button>
                  </div>
                )}
                
                {/* Event Details Card */}
                <div className={`glass-panel rounded-2xl p-6`}>
                  <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    Event Details
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Calendar className="w-5 h-5 text-ux-cyan mt-0.5" />
                      <div>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>Date</p>
                        <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{formatDate(eventDate)}</p>
                      </div>
                    </div>
                    {eventTime && (
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-ux-violet mt-0.5" />
                        <div>
                          <p className={`font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>Time</p>
                          <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{eventTime}</p>
                        </div>
                      </div>
                    )}
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-ux-coral mt-0.5" />
                      <div>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>Location</p>
                        <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                          {event.location || (event.is_online ? 'Online Event' : 'TBA')}
                        </p>
                        {(event.google_maps_url || event.venue_address) && !event.is_online && (
                          <a 
                            href={event.google_maps_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.venue_address + ', ' + (event.location || ''))}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-ux-cyan hover:text-ux-blue transition-colors mt-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            View on Google Maps
                          </a>
                        )}
                      </div>
                    </div>
                    {maxAttendees && (
                      <div className="flex items-start gap-3">
                        <Users className="w-5 h-5 text-ux-amber mt-0.5" />
                        <div>
                          <p className={`font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>Capacity</p>
                          <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{maxAttendees} seats</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Event Registration Form Dialog */}
      <EventRegistrationForm
        event={event}
        isOpen={showRegistrationForm}
        onClose={() => setShowRegistrationForm(false)}
        onSuccess={() => {
          setRegistered(true);
          setShowRegistrationForm(false);
        }}
      />
    </div>
  );
};

export default EventDetailPage;
